"use client";

import { useEffect, useMemo, useState } from "react";
import {
  // CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LightBulbIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import OptimizedImage from "@/components/common/OptimizedImage";
import PhotoCoachExamples from "@/components/listing/PhotoCoachExamples";
import { bindSellerPhotoCoachDevToolbox } from "@/lib/devToolbox/adapters";
import type { ItemImage } from "@/lib/types";

export const SELLER_PHOTO_COACH_DISMISS_KEY = "seller-photo-coach-dismissed-v1";

// const PHOTO_CHECKLIST = [
// {
// id: "main",
// title: "Main photo (search cover)",
// detail:
// "Clear front view of the item — this is what buyers see first in search and cards.",
// },
// {
// id: "detail",
// title: "Detail or label",
// detail:
// "Show important information or issues about the item that buyers should know about.",
// },
// {
// id: "context",
// title: "Angle or context",
// detail:
// "Another angle or photo showing size/scale. All three must be sharp and well lit.",
// },
// ] as const;

interface SellerPhotoCoachProps {
  images: ItemImage[];
  className?: string;
}

function isActiveImage(image: ItemImage): boolean {
  return image.metadata?.__pendingDelete !== true;
}

function readDevDismissed(): boolean {
  if (process.env.NODE_ENV !== "development" || typeof window === "undefined") {
    return false;
  }
  try {
    return sessionStorage.getItem(SELLER_PHOTO_COACH_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export default function SellerPhotoCoach({
  images,
  className = "",
}: SellerPhotoCoachProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [devDismissed, setDevDismissed] = useState(readDevDismissed);

  useEffect(() => {
    return bindSellerPhotoCoachDevToolbox(() =>
      setDevDismissed(readDevDismissed()),
    );
  }, []);

  const activeImages = useMemo(() => images.filter(isActiveImage), [images]);

  const mainImage = useMemo(() => {
    const main = activeImages.find((img) => img.isMain);
    return main ?? activeImages[0] ?? null;
  }, [activeImages]);

  if (devDismissed) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border border-primary-200 bg-primary-50/60 p-4 ${collapsed ? "space-y-0" : "space-y-4"} ${className}`}
      aria-labelledby="seller-photo-coach-heading"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3
            id="seller-photo-coach-heading"
            className="text-sm font-semibold text-primary-900 flex items-center gap-2"
          >
            <PhotoIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
            Photo coach — all 3 photos matter
          </h3>
          {!collapsed ? (
            <p className="mt-1 text-sm text-primary-800/90">
              Upload exactly 3 clear, unique photos of the actual item. Every
              photo must be in focus and well lit — not just the main image.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          className="rounded-md p-1 text-primary-700 hover:bg-primary-100/80"
          aria-expanded={!collapsed}
          aria-controls="seller-photo-coach-body"
          aria-label={collapsed ? "Expand photo coach" : "Collapse photo coach"}
        >
          {collapsed ? (
            <ChevronDownIcon className="h-5 w-5" />
          ) : (
            <ChevronUpIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {!collapsed ? (
        <div id="seller-photo-coach-body" className="space-y-4">
          {/* <ul className="space-y-2">
 {PHOTO_CHECKLIST.map((item, index) => {
 const done = completedCount > index;
 return (
 <li key={item.id} className="flex gap-2 text-sm">
 <CheckCircleIcon
 className={`h-5 w-5 flex-shrink-0 ${done ? "text-primary-600" : "text-text-light"}`}
 aria-hidden
 />
 <div>
 <span className="font-medium text-foreground">
 {item.title}
 </span>
 <span className="text-text-light"> — {item.detail}</span>
 </div>
 </li>
 );
 })}
 </ul> */}

          <div className="flex gap-2 rounded-md bg-surface/80 border border-primary-100 p-3 text-sm text-foreground">
            <LightBulbIcon
              className="h-5 w-5 flex-shrink-0 text-amber-500"
              aria-hidden
            />
            <p>
              Use daylight or a bright room, wipe the lens, and hold steady
              until the image looks sharp. Do not use stock photos or hide the
              item in packaging.
            </p>
          </div>

          <PhotoCoachExamples />

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-text-light mb-2">
              Buyer preview (main photo)
            </p>
            <div className="relative aspect-[4/3] w-full max-w-md rounded-lg overflow-hidden bg-secondary border border-border">
              {mainImage?.url ? (
                <OptimizedImage
                  src={mainImage.url}
                  alt="How your main photo appears to buyers"
                  width={600}
                  height={450}
                  objectFit="contain"
                  className="h-full w-full"
                  usage="detail-main"
                />
              ) : (
                <div className="flex h-full items-center justify-center px-4 text-center text-sm text-text-light">
                  Your main photo will appear here in the same frame buyers see
                  on the item page.
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-text-light">
              Use the star icon to select the main image that will appear first
              in search results. The same quality rules apply to images 2 and 3.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
